import { useState } from 'react'
import { BehaviorEventLog } from './BehaviorEventLog.js'
import { BarrierList } from '../../messages/index.js'
import { MonthPanels } from './MonthPanels.js'
import { HistoryPeriod } from './HistoryPeriod.js'
import { PeriodView, Section, Tabs } from './PeriodView.js'
import { DEFAULT_DAY_RULES, currentMonthOf } from './monthPanelData.js'
import { notificationsInMonth, shiftsInMonth } from './periodData.js'
import type { WorkerDetailHooks } from '../../roster/index.js'
import { isNotFoundError, Skeleton } from '../../core/index.js'
import { LoadFailure } from '../../core/index.js'

function daysUntil(iso: string): number {
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000))
}

type DetailTab = 'current' | 'history'

const DETAIL_TABS: readonly { id: DetailTab; label: string }[] = [
  { id: 'current', label: 'Current' },
  { id: 'history', label: 'History' },
]

interface WorkerScoringPanelProps {
  workerId: string | null
  /** Where the reads come from: the operator app's `/workers/...` or the admin area's `/admin/clients/.../workers/...`. */
  hooks: WorkerDetailHooks
  /** False where a tab above already says "Scoring", as in the operator's roster row. */
  showHeading?: boolean
  /** False where the worker's notes have a tab of their own beside this panel (the operator's roster row); the admin area and the worker app keep them here. */
  showBarriers?: boolean
}

/**
 * What an operator sees when they open a worker's row, and what the
 * platform admin sees for the same worker: the same score dial, breakdown
 * bars and coaching nudges the worker sees in their own app, so a
 * conversation about a score starts from the same picture. One Scoring
 * section in two tabs: the current period, and a history of every month as
 * a panel of its own. The worker's name and role are on the row above.
 *
 * Fetched only while the row is open - `workerId` is null for every
 * collapsed row, which keeps the queries idle rather than loading the whole
 * roster's detail up front.
 */
export function WorkerScoringPanel({ workerId, hooks, showHeading = true, showBarriers = true }: WorkerScoringPanelProps) {
  const profileQuery = hooks.useProfile(workerId)
  const scoreQuery = hooks.useScoreBreakdown(workerId)
  const notificationsQuery = hooks.useNotifications(workerId)
  const outcomesQuery = hooks.useOutcomes(workerId)
  const behaviorEventsQuery = hooks.useBehaviorEvents(workerId)
  const barriersQuery = hooks.useBarriers(workerId)
  const workerScoreQuery = hooks.useScore(workerId)
  const historyQuery = hooks.useScoreHistory(workerId)
  // The facility's grace and short-notice rules decide how a day is shaded in the month calendars; the API's defaults stand in until it loads.
  const configQuery = hooks.useFacilityConfig(profileQuery.data?.facilityId ?? null)
  const [tab, setTab] = useState<DetailTab>('current')
  // The one month open in full under History; closed again when the panel is reopened for another worker, since it remounts.
  const [openMonth, setOpenMonth] = useState<string | null>(null)

  if (workerId === null) {
    return null
  }

  if (profileQuery.isPending || scoreQuery.isPending) {
    return <Skeleton lines={5} label="Loading worker detail" className="px-5 py-6" />
  }

  if (profileQuery.isError || scoreQuery.isError) {
    return <LoadFailure size="xs" className="px-5 py-6" message="Could not load this worker’s detail." onRetry={() => void Promise.all([profileQuery.refetch(), scoreQuery.refetch()])} retrying={profileQuery.isFetching || scoreQuery.isFetching} />
  }

  const profile = profileQuery.data
  // Notifications are supplementary - a worker with none is normal, and
  // failing to load them should not blank out the score breakdown. The
  // current tab shows this month's; every month's are in the history.
  const notifications = notificationsQuery.data ?? []
  const outcomes = outcomesQuery.data ?? []
  const currentMonth = currentMonthOf(historyQuery.data ?? null, new Date())
  const dayRules = configQuery.data?.scoring ?? DEFAULT_DAY_RULES
  const shiftsStatus = outcomesQuery.isPending ? 'pending' : outcomesQuery.isError ? 'error' : undefined

  const monthsSection = (
    <Section title="Score by Month">
      {historyQuery.isPending || outcomesQuery.isPending ? (
        <Skeleton label="Loading months" />
      ) : historyQuery.isError && outcomesQuery.isError ? (
        <LoadFailure size="xs" message="Could not load this worker’s score history or shift outcomes." onRetry={() => void Promise.all([historyQuery.refetch(), outcomesQuery.refetch()])} retrying={historyQuery.isFetching || outcomesQuery.isFetching} />
      ) : (
        <>
          {historyQuery.isError && <LoadFailure size="xs" className="mb-2" message="Could not load this worker’s score history; the months below show shifts only." onRetry={() => void historyQuery.refetch()} retrying={historyQuery.isFetching} />}
          {outcomesQuery.isError && <LoadFailure size="xs" className="mb-2" message="Could not load this worker’s shift outcomes; the months below show scores only." onRetry={() => void outcomesQuery.refetch()} retrying={outcomesQuery.isFetching} />}
          <MonthPanels
            history={historyQuery.data ?? null}
            outcomes={outcomes}
            notifications={notifications}
            heldTier={profile.tier}
            rules={dayRules}
            openMonth={openMonth}
            onOpenMonth={setOpenMonth}
            renderOpen={(month) => (
              <HistoryPeriod key={month} workerId={workerId} useScoreForPeriod={hooks.useScoreForPeriod} month={month} outcomes={outcomes} notifications={notifications} heldTier={profile.tier} rules={dayRules} shiftsStatus={shiftsStatus} />
            )}
          />
        </>
      )}
    </Section>
  )

  return (
    <div className="px-5 py-5 bg-canvas">
      {/* The row above already names the worker and their role; everything in the panel is about the score. */}
      {showHeading && <div className="text-[9px] font-bold uppercase tracking-wide text-ink-mid mb-1.5">Scoring</div>}
      <Tabs tabs={DETAIL_TABS} active={tab} onChange={setTab} />

      {tab === 'history' && monthsSection}

      {tab === 'current' && (
        <>
          <PeriodView
            period={{
              month: currentMonth,
              score: workerScoreQuery.data ?? null,
              breakdown: scoreQuery.data.inputs,
              shifts: shiftsInMonth(outcomes, currentMonth),
              notifications: notificationsInMonth(notifications, currentMonth),
            }}
            dialLabel="Current Score"
            // The on-track tier rides on the score read, which is supplementary: until it loads there is one dial, as before.
            dial={{ score: profile.score, tier: profile.tier, onTrack: workerScoreQuery.data?.onTrack ?? null }}
            dialNote={`Resets in ${daysUntil(profile.scoreResetsAt)} days`}
            rules={dayRules}
            shiftsStatus={shiftsStatus}
          />

          {/* The worker's own account of what is getting in the way, in their words; staff mark each seen and may answer. */}
          {showBarriers && (
          <div className="mt-5">
            <Section title="What’s Getting in the Way — In Their Words">
              {barriersQuery.isPending ? (
                <Skeleton />
              ) : isNotFoundError(barriersQuery.error) ? (
                // The worker loaded, so a 404 here is an API deployed before barriers existed.
                <div className="text-[11px] text-ink-mid">This API does not have barriers yet; deploy it to switch this on.</div>
              ) : barriersQuery.isError ? (
                <LoadFailure size="xs" message="Could not load what this worker has said." onRetry={() => void barriersQuery.refetch()} retrying={barriersQuery.isFetching} />
              ) : (
                <BarrierList
                  barriers={barriersQuery.data}
                  audience="staff"
                  {...hooks.barrierActions}
                  empty="Nothing yet. A worker can write here from their own app once a behavior is costing them points."
                />
              )}
            </Section>
          </div>
          )}

          {/* Shown only once there is something: most clients have no custom behaviors. */}
          {behaviorEventsQuery.data && behaviorEventsQuery.data.length > 0 && (
            <div className="mt-5">
              <Section title="Custom Behavior Events">
                <BehaviorEventLog events={behaviorEventsQuery.data} />
              </Section>
            </div>
          )}
        </>
      )}
    </div>
  )
}
